import { type JSX,useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { IConstructDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { ApiStatusCodes } from 'common/constants/apiStatusCodes';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { useAuthContext } from 'common/contexts/AuthContext';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { UserProfileOemMapping } from 'common/types/Entities';
import {
    EditDrawerContentProps,
    Operations
} from 'common/types/TypeDefs';
import { disableIfMissingAnyRole } from 'common/utils/DisableButtonEvaluator';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { useSnackbar } from 'notistack';

import { UserProfilesDropDown } from '../../common/components/sharedCustomComponents/userProfiles/UserProfilesDropDown';
import { postUser } from '../../common/services/api/Users/Users.thunk';

import { clearFilters } from './usersSlice';

const constructCreateContentComponent = (
    context: IConstructDrawerContext,
    saveButtonFunction: () => void,
    userProfile: UserProfileOemMapping | null,
    handleUserProfileChange: (e: unknown, userProfile: UserProfileOemMapping | null) => void,
    handleUserProfileTextareas: (field: keyof UserProfileOemMapping) => (e: React.ChangeEvent<HTMLInputElement>) => void,
    allowCreateDjUser: boolean,
    djIdpErrorMessage?: string,
): JSX.Element => {
    return <EditDrawer
        title={context.entityName}
        saveButtonFunction={saveButtonFunction}
        processing={context.processing}
    >
        <Stack spacing={2}>
            <UserProfilesDropDown
                value={userProfile}
                allowCreateDjUser={allowCreateDjUser}
                onChange={handleUserProfileChange}
            />
            {userProfile &&
                <>
                    <TextField
                        sx={editDrawerTextFieldStyles()}
                        size="small"
                        label="First Name"
                        variant="filled"
                        value={userProfile.firstName || ''}
                        onChange={handleUserProfileTextareas('firstName')}
                        disabled={!!userProfile.subject}
                    />
                    <TextField
                        sx={editDrawerTextFieldStyles()}
                        size="small"
                        label="Last Name"
                        variant="filled"
                        value={userProfile.lastName || ''}
                        onChange={handleUserProfileTextareas('lastName')}
                        disabled={!!userProfile.subject}
                    />
                    <TextField
                        sx={editDrawerTextFieldStyles()}
                        size="small"
                        label="Company Key"
                        variant="filled"
                        value={userProfile.companyKey ?? ''}
                        onChange={handleUserProfileTextareas('companyKey')}
                        disabled={!!userProfile.subject}
                    />
                    <ReadOnlyTextField label="Identity Provider" value={userProfile.oemIdPName || ''} />
                    {context.nameInConflict &&
                        <Box component='div'
                            display='inline'
                            sx={{
                                color: 'error.main',
                                marginTop: '6px !important',
                                fontSize: '14px'
                            }}>
                            User already exists
                        </Box>
                    }
                </>
            }
            {djIdpErrorMessage &&
                <ErrorHandler
                    errorHeader={'There was an error while creating the DJ IdP Single Sign-on user with the following information:'}
                    errorMessage={djIdpErrorMessage}
                    shouldShowCopyIcon
                >
                    <div style={{ fontFamily: 'Consolas, "courier new"' }}>
                        <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                            {djIdpErrorMessage && JSON.stringify(JSON.parse(djIdpErrorMessage), null, 2)}
                        </pre>
                    </div>
                </ErrorHandler>
            }
        </Stack>
    </EditDrawer>;
};

export function UserCreateContent({
    title,
}: EditDrawerContentProps): JSX.Element {
    const [userProfile, setUserProfile] = useState<UserProfileOemMapping | null>(null);
    const [djIdpErrorMessage, setDjIdpErrorMessage] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const { enqueueSnackbar } = useSnackbar();
    const nameInConflict = useAppSelector(selectNameInConflict);
    const navigate = useNavigate();

    const { currentUserRoles } = useAuthContext();
    const allowedCreateDjUser = [Roles.Admin, Roles.UserManager];
    const allowCreateDjUser = !disableIfMissingAnyRole(currentUserRoles, allowedCreateDjUser);

    useEffect(() => {
        if(currentId === DrawerIDs.Users){
            setUserProfile(null);
            dispatch(setNameInConflict(false));
            setDjIdpErrorMessage('');
        }
    }, [drawerOpen]);

    useEffect(() => {
        setSaveButtonAvailability();
        dispatch(setNameInConflict(false));
        setDjIdpErrorMessage('');
    }, [userProfile]);

    const handleUserProfileChange = (_e: unknown, profile: UserProfileOemMapping | null) => {
        setUserProfile(profile);
    };

    const handleUserProfileTextareas = (field: keyof UserProfileOemMapping) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (userProfile) {
            setUserProfile({ ...userProfile, [field]: e.target.value });
        }
    };

    const setSaveButtonAvailability = () => {
        const REQUIRED_USER_PROFILE_FIELDS = ['firstName', 'lastName', 'email', 'companyKey'];
        const existsInOem = !!userProfile?.oemId;
        let enabled = false;
        if(!existsInOem) {
            const hasSubject = !!userProfile?.subject;
            const hasRequiredFields = REQUIRED_USER_PROFILE_FIELDS.every(field => !!userProfile?.[field as keyof UserProfileOemMapping]);
            enabled = hasSubject || hasRequiredFields;
        }
        dispatch(setSaveButtonEnabled(enabled));
    };

    const _isDJIdpClientError = (statusCode: number): boolean => {
        return statusCode === ApiStatusCodes.DJ_IDP_CLIENT_ERROR;
    };

    const _omitDJIdpErrorPrefix = (error: string): string => {
        const PREFIX = 'Internal API error: 570. ';

        if (error.startsWith(PREFIX)) {
            return error.slice(PREFIX.length);
        }
        return error;
    };

    const paginationGridData = useAppSelector(selectPaginationGridData);
    const userPostCallback = () => {
        setProcessing(true);
        const userOptions = {
            post: {
                issuer: userProfile?.authority ?? '',
                subject: userProfile?.subject,
                profile: {
                    firstName: userProfile?.firstName,
                    lastName: userProfile?.lastName,
                    email: userProfile?.email,
                    companyKey: userProfile?.companyKey
                }
            },
            take: paginationGridData?.rowsPerPage,
            sortKey: paginationGridData?.sortModel?.[0]?.field,
            sortOrder: paginationGridData?.sortModel?.[0]?.sort,
        };
        dispatch(clearFilters());
        dispatch(postUser(userOptions))
            .unwrap()
            .then((result) => {
                dispatch(closeDrawer());
                enqueueSnackbar('The User was successfully created.', { variant: 'success' });
                dispatch(setPaginationGridData({
                    rows: result.rows,
                    rowsCount: result.count,
                    currentPage: paginationGridData?.currentPage,
                    rowsPerPage: paginationGridData?.rowsPerPage,
                    sortModel: paginationGridData?.sortModel,
                }));
                navigate(`/${Routes.Users}/${result.response.data.userId}`);
            })
            .catch((error) => {
                const errorStatusCode = extractStatusCode(error.message);
                if (_isDJIdpClientError(errorStatusCode)) {
                    const djIdpError = _omitDJIdpErrorPrefix(error.message);
                    setDjIdpErrorMessage(djIdpError);
                } else {
                    handleEditDrawerError(Operations.Create, extractStatusCode(error.message), 'User', dispatch, enqueueSnackbar);
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        {
            entityName: title,
            nameInConflict: !!nameInConflict,
            processing
        },
        userPostCallback,
        userProfile,
        handleUserProfileChange,
        handleUserProfileTextareas,
        allowCreateDjUser,
        djIdpErrorMessage
    );
}
